/**
 * useUnsavedChangesGuard — two-layer navigate-away guard for dirty pages
 * (spec: docs/superpowers/specs/2026-07-13-workshop-save-ux-design.md, §c).
 *
 * Layer 1 — `beforeunload` (refresh / tab close / external nav): registered
 * while `when === true`; standard preventDefault + returnValue so the browser
 * shows its native leave-confirmation.
 *
 * Layer 2 — in-app navigation blocking via React Router's `useBlocker`, which
 * requires a data router (main.tsx provides one via createBrowserRouter with
 * a single splat route). Availability is detected with a safe context read of
 * `UNSAFE_DataRouterContext`; when absent (e.g. unit tests rendering under
 * MemoryRouter) the hook degrades to beforeunload-only. `useBlocker` itself
 * lives in an inner bridge component that is only MOUNTED when the context
 * exists — hooks are never called conditionally.
 *
 * Usage:
 *   const guard = useUnsavedChangesGuard({ when: dirty && !saving })
 *   // render guard.element once anywhere on the page (null without a data
 *   // router), then drive a confirm UI from guard.blocked with
 *   // guard.proceed() (continue the navigation) / guard.reset() (stay).
 */
import {
  createElement,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactElement,
} from 'react'
import {
  UNSAFE_DataRouterContext,
  useBlocker,
  type Blocker,
  type BlockerFunction,
} from 'react-router-dom'

export interface UseUnsavedChangesGuardOptions {
  /** Guard is armed while true (e.g. `dirty && !saving`). */
  when: boolean
  /**
   * Narrows which in-app navigations block while armed (e.g. let a same-page
   * `?tab=` replace through). Omitted = every navigation blocks.
   * `beforeunload` follows `when` alone.
   */
  shouldBlock?: BlockerFunction
}

export interface UnsavedChangesGuardResult {
  /** True when an in-app navigation attempt has been blocked. */
  blocked: boolean
  /** Continue the blocked navigation. */
  proceed: () => void
  /** Cancel the blocked navigation and stay. */
  reset: () => void
  /**
   * Bridge element that hosts the router blocker — render it once on the
   * page. `null` when no data router is available (beforeunload-only mode).
   */
  element: ReactElement | null
}

interface BridgeProps {
  when: boolean
  shouldBlock?: BlockerFunction
  onBlockerChange: (blocker: Blocker) => void
}

/**
 * Inner component so `useBlocker` (which throws without a data router) is
 * only ever called when `UNSAFE_DataRouterContext` is present — the hook
 * conditionally MOUNTS this component, never conditionally calls hooks.
 */
function DataRouterBlockerBridge({ when, shouldBlock, onBlockerChange }: BridgeProps) {
  const blocker = useBlocker(!when ? false : (shouldBlock ?? true))
  useEffect(() => {
    onBlockerChange(blocker)
  }, [blocker, onBlockerChange])
  return null
}

export function useUnsavedChangesGuard({
  when,
  shouldBlock,
}: UseUnsavedChangesGuardOptions): UnsavedChangesGuardResult {
  // Layer 1: beforeunload while armed.
  useEffect(() => {
    if (!when) return
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // Legacy engines require returnValue to be set for the prompt to show.
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [when])

  // Layer 2: in-app blocking, only when a data router is present.
  const dataRouterContext = useContext(UNSAFE_DataRouterContext)
  const [blocker, setBlocker] = useState<Blocker | null>(null)

  const proceed = useCallback(() => {
    blocker?.proceed?.()
  }, [blocker])
  const reset = useCallback(() => {
    blocker?.reset?.()
  }, [blocker])

  const element = dataRouterContext
    ? createElement(DataRouterBlockerBridge, { when, shouldBlock, onBlockerChange: setBlocker })
    : null

  return { blocked: blocker?.state === 'blocked', proceed, reset, element }
}
